import '@Resources/fonts/fonts.css';
import '@Resources/styles/app.css';
import { theme } from '@Styles/MuiTheme';
import LayoutStyles from '@Styles/layout.scss';
import { AppState, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { _isNil } from 'lodash/isNil';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RequireAuth from '@Services/authentication/RequireAuth';
import { AxiosClient } from '@Services/data/AxiosClient';
import { KoruRoutes } from '@Services/navigation/Paths';
import configureStore from '@Services/redux/Store';
import RootBaseView from '@Views/BaseViews/Root';
import { WorkbenchBaseView } from '@Views/BaseViews/Workbench';
import HomeView from '@Views/Home/HomeView';
import LoginView from '@Views/Login/LoginView';
import ProjectView from '@Views/Project/ProjectView';
import { QualityControlView } from '@Views/QualityControl/QualityControlView';

const VersionComponent = () => {
    return (
        <div>
            <div>
                <td>version</td>
                <td>{KORU_VERSION}</td>
            </div>
            <div>
                <td>build</td>
                <td>{KORU_BUILD}</td>
            </div>
        </div>
    );
};

export default function App() {
    const { getAccessTokenSilently, user } = useAuth0();
    useEffect(() => {
        const setToken = async () => {
            AxiosClient.instance.setTokenGenerator(getAccessTokenSilently);
            AxiosClient.instance.setUser(user);
        };
        setToken();
    }, [getAccessTokenSilently, user]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Routes>
                <Route
                    path={KoruRoutes.version.href}
                    element={<RequireAuth component={VersionComponent} />}
                />
                <Route path={KoruRoutes.login.href} element={<LoginView />} />
                <Route
                    path={KoruRoutes.root.href}
                    element={<RequireAuth component={RootBaseView} />}
                >
                    <Route
                        index
                        element={<RequireAuth component={HomeView} />}
                    />
                    <Route
                        path={KoruRoutes.workbenchRoot.href}
                        element={<RequireAuth component={WorkbenchBaseView} />}
                    >
                        <Route
                            path={KoruRoutes.project.href}
                            element={<RequireAuth component={ProjectView} />}
                        />
                        <Route
                            path={KoruRoutes.qc.href}
                            element={
                                <RequireAuth component={QualityControlView} />
                            }
                        />
                    </Route>
                </Route>
            </Routes>
        </LocalizationProvider>
    );
}

export function renderToDom() {
    const rootHtml = document.getElementById('root');
    const rootRenderer = createRoot(rootHtml);
    const logError = (error: Error, info: { componentStack: string }) => {
        // Do something with the error, e.g. log to an external API
        console.log('%s %n %s', error, JSON.stringify(info));
    };
    function Fallback({ error, resetErrorBoundary }) {
        // Call resetErrorBoundary() to reset the error boundary and retry the render.

        return (
            <div role="alert">
                <p>Something went wrong:</p>
                <pre style={{ color: 'red' }}>{error.message}</pre>
            </div>
        );
    }

    const Auth0ProviderWithRedirectCallback = ({ children, ...props }) => {
        const navigate = useNavigate();
        const onRedirectCallback = (appState) => {
            try {
                navigate(
                    (appState && appState.returnTo) || window.location.pathname
                );
            } catch (e) {
                console.error(e);
            }
        };
        return (
            <Auth0Provider
                domain={process.env.AUTH0_DOMAIN}
                clientId={process.env.AUTH0_CLIENT_ID}
                authorizationParams={{
                    audience: process.env.AUTH0_AUDIENCE,
                    redirect_uri: process.env.BASE_URI,
                }}
                onRedirectCallback={onRedirectCallback}
                {...props}
            >
                {children}
            </Auth0Provider>
        );
    };

    rootRenderer.render(
        <Provider store={configureStore}>
            <BrowserRouter>
                <Auth0ProviderWithRedirectCallback>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <ErrorBoundary
                            FallbackComponent={Fallback}
                            onError={logError}
                        >
                            <App />
                        </ErrorBoundary>

                        <ToastContainer
                            style={{ marginTop: LayoutStyles.headerHeight }}
                        />
                    </ThemeProvider>
                </Auth0ProviderWithRedirectCallback>
            </BrowserRouter>
        </Provider>
    );
}
